import React, { useCallback, useState } from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useAttachExperience, useGetEligibleExperiences } from '~/hooks/apis/accommodation/useBundles';
import useDebounced from '~/hooks/useDebounced';

import { EligibleExperience } from '~/services/AccommodationService';

interface Props {
  bundleId: string;
  open: boolean;
  onClose: () => void;
}

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_CHARS = 3;
const PAGE_SIZE = 50;

function AccommodationBundleAttachExperienceDialog({ bundleId, open, onClose }: Props) {
  const [selected, setSelected] = useState<EligibleExperience | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const debouncedSearch = useDebounced(searchInput, SEARCH_DEBOUNCE_MS);
  const hasMinChars = debouncedSearch.trim().length >= MIN_SEARCH_CHARS;

  const eligibleExperiencesQuery = useGetEligibleExperiences({
    search: debouncedSearch,
    limit: PAGE_SIZE,
    // Gate on min chars so we don't load every eligible experience on dialog open;
    // also avoids hammering svc-experience for single-character typeahead inputs.
    enabled: open && hasMinChars,
  });

  const attachExperienceMutation = useAttachExperience();

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!selected) return;
      setErrorMessage(null);
      try {
        await attachExperienceMutation.mutateAsync({
          bundleId,
          payload: { experienceId: selected.id },
        });
        onClose();
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Failed to attach experience';
        setErrorMessage(msg);
      }
    },
    [attachExperienceMutation, bundleId, selected, onClose],
  );

  const isSubmitting = attachExperienceMutation.isPending;
  const isLoadingOptions = eligibleExperiencesQuery.isFetching;
  const options = hasMinChars ? eligibleExperiencesQuery.data?.experiences ?? [] : [];
  const total = eligibleExperiencesQuery.data?.total ?? 0;
  const noOptionsText = hasMinChars
    ? 'No eligible experiences match'
    : `Type at least ${MIN_SEARCH_CHARS} characters to search`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Attach Experience</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Select a bundle-eligible experience. Only experiences with `allowAccomBundle` are listed.
            </Typography>
            <Autocomplete<EligibleExperience>
              options={options}
              value={selected}
              onChange={(_event, value) => setSelected(value)}
              inputValue={searchInput}
              onInputChange={(_event, value, reason) => {
                // Ignore the 'reset' fired when an option is selected; otherwise the input
                // would clear and the dropdown would re-open with default results.
                if (reason !== 'reset') setSearchInput(value);
                // Clear the staged selection when the user keeps typing — otherwise the
                // Attach button would stay enabled and submit a stale `selected` even
                // though the visible input no longer matches that option's title.
                if (reason === 'input') setSelected(null);
              }}
              getOptionLabel={(option) => option.title}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              filterOptions={(x) => x}
              loading={hasMinChars && isLoadingOptions}
              noOptionsText={noOptionsText}
              autoFocus
              fullWidth
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.id}
                      {option.maxBundleDiscountPct != null ? ` · up to ${option.maxBundleDiscountPct}% off` : ''}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Experience"
                  placeholder={`Type ${MIN_SEARCH_CHARS}+ characters to search by title…`}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {hasMinChars && isLoadingOptions ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  required
                />
              )}
            />
            {total > options.length && (
              <Typography variant="caption" color="text.secondary">
                Showing {options.length} of {total}. Refine your search to narrow.
              </Typography>
            )}
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={!selected || isSubmitting}>
            {isSubmitting ? 'Attaching…' : 'Attach'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AccommodationBundleAttachExperienceDialog;
